import React, { useEffect, useState } from "react";
import { TagDwr } from "../../../dto/TagDwr";
import { ResourceDWR } from "../../../dto/ResourceDWR";
import { useLanguage } from "../../Language";
import {
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useAccount } from "../../Account";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import axios from "axios";
import { LoginClientManager } from "@tlc/ls2pac/app/ClientManagers/LoginClientManager";
import { fetchAllTags, TagCount } from "../config/TagManagementPage";
import { useLocationConfiguration } from "../../LocationConfiguration";
import { useNavigate } from "react-router-dom";

export interface TagDisplayProps {
  resource: ResourceDWR;
}

interface TagFormInput {
  tag: string;
}

interface TagView {
  id: number;
  data: string;
  blocked: boolean;
  userEntered: boolean;
  count: number;
}

interface AddTag {
  resourceId: number;
  tag: string;
  userEntered: boolean;
}

export const TagDisplay = ({ resource }: TagDisplayProps) => {
  const { translate } = useLanguage();
  const navigate = useNavigate();
  const { isAuthenticated, isConfigAdmin } = useAccount();
  const { locationConfiguration } = useLocationConfiguration();

  const getTagSearchURL = (tag: string) => {
    const params = new URLSearchParams();
    params.set("term", `tag:${tag}`);
    return `/search/ls?${params.toString()}`;
  };

  const handleDelete = async (tagId: number) => {
    if (!isConfigAdmin) {
      return;
    }

    try {
      const { data } = await axios.delete<boolean>(
        `/tag/${resource.id}/${tagId}`
      );

      if (data) {
        setTags(tags.filter((t) => t.id !== tagId));
      } else {
        // Improvement: error handling
      }
    } catch (e: unknown) {
      // Improvement: error handling
    }
    return false;
  };
  const handleClose = () => {
    reset();
    setAddTagOpen(false);
  };

  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  const handleOpen = () => {
    setOpen(true);
    (async () => {
      setLoading(true);
      // make the request to the backend for tags
      const allTags = await fetchAllTags();
      setTagOptions(allTags);
      setLoading(false);
    })();
  };

  const handleACClose = () => {
    setOpen(false);
    setTagOptions([]);
  };

  const [tagOptions, setTagOptions] = useState<TagCount[]>([]);

  const onClickAddTag = async () => {
    reset(); // reset on open in case the blur on close triggered a validation error after reset
    if (isAuthenticated) {
      setAddTagOpen(true);
    } else {
      // Improvement: replace LoginClientManager with a react context function
      // ensure it handles SSO (w/ and w/o Guardian) and Sign in with Google workflows
      LoginClientManager.promptIfNotLoggedIn(() => {
        setAddTagOpen(true);
      });
    }
  };
  const formMethods = useForm<TagFormInput>({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      tag: "",
    },
  });
  const { handleSubmit, reset, setFocus, control } = formMethods;

  const [addTagOpen, setAddTagOpen] = useState<boolean>(false);
  const [tags, setTags] = useState<TagDwr[]>(resource.tags);

  const onSubmit: SubmitHandler<TagFormInput> = async ({ tag }) => {
    const postData: AddTag = {
      resourceId: resource.id,
      tag: tag.trim(),
      userEntered: true,
    };
    try {
      const { data } = await axios.post<TagView>(`/tag`, postData);

      if (tags.some((t) => t.id === data.id)) {
        // Tag already added
        // Improvement: alternate success/notice toast
        setAddTagOpen(false);
        reset();
        return;
      }
      // Improvement: success toast
      setTags([...tags, data]);
      setAddTagOpen(false);
      reset();
    } catch (e: unknown) {
      // Improvement: error handling
    }
  };

  useEffect(() => {
    if (addTagOpen) {
      const timeout = setTimeout(() => setFocus("tag"), 0);
      return () => clearTimeout(timeout);
    }
  }, [addTagOpen, setFocus]);

  // tag column limit is 10,000
  // tag_data_idx is 2704 bytes
  // don't want tag search URLs to exceed 2000 characters
  // 200 seemed like a sensible upper limit
  const TAG_MAX_LENGTH = 200;
  const label = translate("advancedSearch_tag");

  if (!locationConfiguration?.pacSettings?.userActionSettings?.allowTagging) {
    return null;
  }

  return (
    <>
      <Dialog open={addTagOpen} onClose={handleClose}>
        <FormProvider {...formMethods}>
          <form noValidate onSubmit={handleSubmit(onSubmit)}>
            <DialogTitle>{translate("addNewTag")}</DialogTitle>
            <DialogContent>
              <Controller
                name={"tag"}
                control={control}
                rules={{
                  required: `${translate("isRequired", {
                    LABEL: label,
                  })}`,
                  maxLength: {
                    value: TAG_MAX_LENGTH,
                    message: `${translate("maxLengthError", {
                      LABEL: label,
                      MAX_LENGTH: TAG_MAX_LENGTH.toString(),
                    })}`,
                  },
                }}
                render={({
                  field: { ref, value, onChange, ...field },
                  fieldState: { error },
                }) => (
                  <Autocomplete
                    sx={{ mt: 1, minWidth: "240px" }}
                    options={tagOptions.map((option) => option.tag)}
                    freeSolo
                    open={open}
                    loading={loading}
                    onOpen={handleOpen}
                    onClose={handleACClose}
                    autoSelect
                    fullWidth
                    {...field}
                    value={value}
                    onChange={(_, value, reason) => {
                      if (reason === "clear") {
                        onChange(null);
                      } else if (reason === "selectOption") {
                        onChange(value);
                      } else {
                        // reason "createOption" user entered
                        // "blur" could be an existing selection or user input
                        // confirm a case insensitive match doesn't already exist
                        // tags are removed insensitive to case
                        // (removing "test" removes "Test")
                        const input = (value as string).toLowerCase().trim();
                        const existing = tagOptions.find(
                          (to) => to.tag.toLowerCase().trim() === input
                        );
                        if (existing) {
                          onChange(existing.tag); // prevent duplicates
                        } else {
                          onChange(value.trim()); // auto trim the value
                        }
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        error={!!error}
                        helperText={error ? <>{error.message}</> : " "}
                        label={translate("advancedSearch_tag")}
                        inputRef={ref}
                      />
                    )}
                  />
                )}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>{translate("cancel")}</Button>
              <Button type="submit">{translate("add")}</Button>
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>
      <Typography variant="h6" component={"h4"}>
        {translate("tags")}
      </Typography>
      <ul
        style={{
          listStyle: "none",
          display: "flex",
          flexWrap: "wrap",
          margin: 0,
        }}
      >
        {tags.map((tag) => (
          <li
            style={{ margin: "8px 4px", maxWidth: "100%" }}
            key={`tag${tag.id}`}
          >
            {/* Using an anchor component allows open in new tab behavior */}
            {/* onClick is needed for button behavior */}
            {/* need to prevent default onDelete handler */}
            <Chip
              label={tag.data}
              variant="outlined"
              component="a"
              href={`/#${getTagSearchURL(tag.data)}`}
              onClick={(event) => {
                navigate(getTagSearchURL(tag.data));
                event.stopPropagation();
                event.preventDefault();
              }}
              onDelete={
                isConfigAdmin
                  ? (event) => {
                      handleDelete(tag.id);
                      // Prevent following the link
                      event.stopPropagation();
                      event.preventDefault();
                    }
                  : undefined
              }
            />
          </li>
        ))}
        <li style={{ margin: "8px 4px" }}>
          <Chip
            variant="outlined"
            onClick={onClickAddTag}
            label={translate("addNewTag")}
            color="primary"
            icon={<AddIcon />}
          />
        </li>
      </ul>
    </>
  );
};
